import { Helmet } from "react-helmet-async";

interface SchemaMarkupProps {
  type?: 'homepage' | 'about' | 'pricing' | 'seo-services' | 'resources' | 'service';
  title?: string;
  description?: string;
  url?: string;
}

const SchemaMarkup = ({ 
  type = 'homepage', 
  title, 
  description, 
  url 
}: SchemaMarkupProps) => {
  const baseUrl = "https://vincentgrowthpartners.com";
  const currentUrl = url || baseUrl;

  // LocalBusiness Schema (2025 Enhanced)
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${baseUrl}#organization`,
    "name": "Vincent Growth Partners",
    "alternateName": "VGP Marketing",
    "description": "Premium digital marketing and SEO services for ambitious businesses. We help companies scale through proven lead generation systems and data-driven growth strategies.",
    "url": baseUrl,
    "telephone": "+447857450763",
    "email": "info@vincentgrowthpartners.com",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "GB",
      "addressRegion": "England"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "51.5074",
      "longitude": "-0.1278"
    },
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "51.5074",
        "longitude": "-0.1278"
      },
      "geoRadius": "100000"
    },
    "areaServed": ["United Kingdom", "London", "Manchester", "Birmingham", "Leeds", "Liverpool"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Digital Marketing Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "SEO Services",
            "description": "Comprehensive search engine optimization to improve online visibility and organic traffic",
            "provider": {
              "@id": `${baseUrl}#organization`
            }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service", 
            "name": "Lead Generation",
            "description": "Targeted lead generation systems to attract high-quality prospects and increase conversions",
            "provider": {
              "@id": `${baseUrl}#organization`
            }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Digital Growth Strategy",
            "description": "Comprehensive digital growth frameworks and proven systems for business scaling",
            "provider": {
              "@id": `${baseUrl}#organization`
            }
          }
        }
      ]
    },
    "foundingDate": "2023",
    "slogan": "Scale Your Business With Proven SEO & Lead Generation Systems",
    "priceRange": "£££",
    "hasCredential": [
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "Professional Certification",
        "name": "Google Ads Certification"
      },
      {
        "@type": "EducationalOccupationalCredential", 
        "credentialCategory": "Professional Certification",
        "name": "Google Analytics Certification"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "87",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "author": {
          "@type": "Person",
          "name": "Sarah Johnson"
        },
        "reviewBody": "VGP transformed our digital presence. We've seen a 150% increase in qualified leads within 90 days.",
        "datePublished": "2024-12-15"
      },
      {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "author": {
          "@type": "Person",
          "name": "Michael Thompson"
        },
        "reviewBody": "Outstanding ROI and professional service. Their SEO strategies delivered measurable results quickly.",
        "datePublished": "2024-11-28"
      }
    ],
    "sameAs": [
      "https://www.linkedin.com/company/vincent-growth-partners",
      "https://twitter.com/vgpmarketing",
      "https://www.facebook.com/vincentgrowthpartners"
    ]
  };

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${baseUrl}#organization`,
    "name": "Vincent Growth Partners",
    "url": baseUrl,
    "logo": {
      "@type": "ImageObject",
      "url": `${baseUrl}/src/assets/vgp-logo.png`,
      "width": "400",
      "height": "100"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+447857450763",
      "contactType": "customer service",
      "areaServed": "GB",
      "availableLanguage": "English"
    }
  };

  // Website Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${baseUrl}#website`,
    "url": baseUrl,
    "name": "Vincent Growth Partners",
    "description": "Premium digital marketing and SEO services for ambitious businesses",
    "publisher": {
      "@id": `${baseUrl}#organization`
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${baseUrl}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  // Service Schema for SEO Services page
  const seoServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Professional SEO Services",
    "description": "Comprehensive SEO services including local SEO, technical SEO, content optimization, and digital growth strategies for businesses looking to increase online visibility and revenue.",
    "provider": {
      "@id": `${baseUrl}#organization`
    },
    "areaServed": ["United Kingdom", "London", "Manchester", "Birmingham"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "SEO Service Packages",
      "itemListElement": [
        {
          "@type": "Offer",
          "name": "Local SEO Optimization",
          "description": "Complete local SEO package including Google Business Profile optimization, local citation building, and geo-targeted content creation.",
          "price": "1500",
          "priceCurrency": "GBP",
          "priceValidUntil": "2025-12-31"
        },
        {
          "@type": "Offer", 
          "name": "Technical SEO Audit",
          "description": "Comprehensive technical SEO analysis covering Core Web Vitals, site architecture, and performance optimization.",
          "price": "2500",
          "priceCurrency": "GBP",
          "priceValidUntil": "2025-12-31"
        }
      ]
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "price": "1500",
      "priceCurrency": "GBP",
      "priceValidUntil": "2025-12-31",
      "url": `${baseUrl}/seo-local-search`
    }
  };

  // FAQ Schema for better voice search optimization
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How long does it take to see SEO results?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most businesses see measurable SEO improvements within 90 days, with significant ranking improvements typically occurring within 3-6 months of implementing our strategies."
        }
      },
      {
        "@type": "Question",
        "name": "What makes Vincent Growth Partners different from other digital marketing agencies?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We focus exclusively on premium businesses and use proven, data-driven strategies that consistently deliver 150% average revenue increases. Our clients maintain a 100% satisfaction rate due to our personalized approach and measurable results."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer local SEO services?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, we specialize in local SEO optimization including Google Business Profile management, local citation building, review management, and geo-targeted content strategies to help businesses dominate their local market."
        }
      },
      {
        "@type": "Question",
        "name": "What is included in your SEO audit?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our comprehensive SEO audit includes technical analysis, Core Web Vitals assessment, content optimization review, local SEO evaluation, competitor analysis, and a detailed action plan with priority recommendations."
        }
      }
    ]
  };

  // BreadcrumbList Schema for navigation
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      }
    ]
  };

  const getSchemas = (): any[] => {
    const schemas: any[] = [organizationSchema, websiteSchema];
    
    switch (type) {
      case 'homepage':
        schemas.push(localBusinessSchema, faqSchema, breadcrumbSchema);
        break;
      case 'seo-services':
        schemas.push(seoServiceSchema, faqSchema);
        break;
      case 'about':
      case 'pricing':
      case 'resources':
        schemas.push(breadcrumbSchema);
        break;
      default:
        schemas.push(localBusinessSchema);
    }
    
    return schemas;
  };

  return (
    <Helmet>
      {getSchemas().map((schema, index) => (
        <script key={`schema-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SchemaMarkup;